import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  IconButton,
  Box,
  Chip
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Edit as EditIcon,
  EventRepeat as ReservationIcon
} from '@mui/icons-material';
import { ZulassungsantragResponse } from '../../types/antrag';
import { Kennzeichen } from '../../types/fahrzeug';

interface KennzeichendatenCardProps {
  antrag: ZulassungsantragResponse;
  onEdit: () => void;
  formatKennzeichen: (kennzeichen?: Kennzeichen | null) => string;
}

const KennzeichendatenCard: React.FC<KennzeichendatenCardProps> = ({
  antrag,
  onEdit,
  formatKennzeichen
}) => {
  const isUmschreibung =
    antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_OHNE_HALTERWECHSEL' ||
    antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_ANDERER_BEZIRK_MIT_HALTERWECHSEL' ||
    antrag.geschaeftsvorfallTyp === 'UMSCHREIBUNG_INNERHALB_MIT_HALTERWECHSEL';

  const useOldVornHinten =
    antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' ||
    antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG' ||
    antrag.geschaeftsvorfallTyp === 'WIEDERZULASSUNG_SELBER_HALTER' ||
    isUmschreibung;

  const altesKennzeichenAnzeige: Kennzeichen | null | undefined =
    antrag.fahrzeug?.altesKennzeichenVorn || antrag.fahrzeug?.altesKennzeichenHinten || antrag.fahrzeug?.altesKennzeichen;

  const hasOnlyVorn = !!antrag.fahrzeug?.altesKennzeichenVorn && !antrag.fahrzeug?.altesKennzeichenHinten;
  const hasOnlyHinten = !antrag.fahrzeug?.altesKennzeichenVorn && !!antrag.fahrzeug?.altesKennzeichenHinten;

  const renderKennzeichen = (kennzeichen?: Kennzeichen | null, sonderkennzeichen?: string | null) => {
    if (!kennzeichen || !kennzeichen.bezirk) {
      return (
        <Typography variant="body2" color="textSecondary" fontStyle="italic">
          Nicht verfügbar
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          border: '3px solid #000',
          borderRadius: '8px',
          backgroundColor: '#fff',
          padding: '8px 12px',
          fontFamily: '"FE-Schrift", "Arial Narrow", sans-serif',
          fontSize: '2rem',
          fontWeight: 'bold',
          letterSpacing: '2px',
          position: 'relative',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          maxWidth: 'fit-content'
        }}
      >
        {/* EU-Streifen */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '35px',
            backgroundColor: '#003399',
            borderRadius: '5px 0 0 5px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.5rem',
            fontWeight: 'normal'
          }}
        >
          {/* EU-Sternenkreis */}
          <Box
            sx={{
              position: 'relative',
              width: '24px',
              height: '24px',
              marginBottom: '4px'
            }}
          >
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 - 90) * (Math.PI / 180);
              const x = 12 + 10 * Math.cos(angle);
              const y = 12 + 10 * Math.sin(angle);
              return (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    width: '3px',
                    height: '3px',
                    borderRadius: '50%',
                    backgroundColor: '#FFD700',
                    left: `${x}px`,
                    top: `${y}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              );
            })}
          </Box>
          <Box sx={{ fontSize: '1rem', fontWeight: 'bold', letterSpacing: '0' }}>D</Box>
        </Box>
        
        {/* Kennzeichen-Text */}
        <Box sx={{ marginLeft: '45px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box component="span" sx={{ color: '#000' }}>
            {kennzeichen.bezirk}
          </Box>
          {/* TÜV-Plakette (grauer Kreis) - ersetzt den Trenner */}
          <Box
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              backgroundColor: '#888',
              border: '2px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.55rem',
              color: '#fff',
              fontWeight: 'bold',
              margin: '0 4px',
              flexShrink: 0
            }}
          >
            TÜV
          </Box>
          <Box component="span" sx={{ color: '#000' }}>
            {kennzeichen.buchstaben} {kennzeichen.nummer}
          </Box>
          {sonderkennzeichen && (
            <Box
              component="span"
              sx={{
                marginLeft: '8px',
                padding: '2px 6px',
                backgroundColor: sonderkennzeichen === 'E' ? '#00ff00' : '#000',
                color: sonderkennzeichen === 'E' ? '#000' : '#fff',
                borderRadius: '4px',
                fontSize: '1.5rem'
              }}
            >
              {sonderkennzeichen}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<CarIcon />}
        title="Kennzeichendaten"
        subheader="Aktuelles und vorheriges Kennzeichen"
        action={
          <IconButton 
            onClick={onEdit}
            size="small"
            color="primary"
          >
            <EditIcon />
          </IconButton>
        }
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Neues Kennzeichen:
            </Typography>
            {renderKennzeichen(
              antrag.fahrzeug?.neuesKennzeichen,
              antrag.fahrzeug?.neuesKennzeichen?.sonderkennzeichen
            )}
          </Grid>
          {useOldVornHinten ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Altes Kennzeichen:
              </Typography>
              {(hasOnlyVorn || hasOnlyHinten) && (
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {hasOnlyVorn ? 'Nur vorne' : 'Nur hinten'}
                </Typography>
              )}
              {renderKennzeichen(
                altesKennzeichenAnzeige,
                altesKennzeichenAnzeige?.sonderkennzeichen
              )}
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {antrag.fahrzeug?.altesKennzeichenVorn?.siegelSicherheitscode && (
                  <Typography variant="body2" color="textSecondary">
                    Siegel-Code Vorn: <strong>{antrag.fahrzeug.altesKennzeichenVorn.siegelSicherheitscode}</strong>
                  </Typography>
                )}
                {antrag.fahrzeug?.altesKennzeichenHinten?.siegelSicherheitscode && (
                  <Typography variant="body2" color="textSecondary">
                    Siegel-Code Hinten: <strong>{antrag.fahrzeug.altesKennzeichenHinten.siegelSicherheitscode}</strong>
                  </Typography>
                )}
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Altes Kennzeichen:
              </Typography>
              {renderKennzeichen(
                antrag.fahrzeug?.altesKennzeichen,
                antrag.fahrzeug?.altesKennzeichen?.sonderkennzeichen
              )}
              {antrag.fahrzeug?.altesKennzeichen?.siegelSicherheitscode && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Siegel-Code: <strong>{antrag.fahrzeug.altesKennzeichen.siegelSicherheitscode}</strong>
                </Typography>
              )}
            </Grid>
          )}
          {/* Fahrzeugbezogene Kennzeichenreservierung - nur bei Außerbetriebsetzung */}
          {antrag.geschaeftsvorfallTyp === 'AUSSERBETRIEBSETZUNG' && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReservationIcon fontSize="small" color={antrag.kennzeichenReservierung ? 'primary' : 'disabled'} />
                <Typography variant="body2" color="textSecondary">
                  Fahrzeugbezogene Kennzeichenreservierung:
                </Typography>
                <Chip
                  label={antrag.kennzeichenReservierung ? 'Ja (max. 12 Monate)' : 'Nein'}
                  size="small"
                  color={antrag.kennzeichenReservierung ? 'success' : 'default'}
                  variant={antrag.kennzeichenReservierung ? 'filled' : 'outlined'}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default KennzeichendatenCard;
